import { useCallback } from "react";
import type { CustomChannelMetrics } from "@/hooks/api/use-custom-channels";
import { buildFilterGroupFromColumnState } from "@/lib/filters";
import type { QueryParams } from "@/lib/process-query-params";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import type { CustomChannel } from "./api/use-custom-channels";
import { type BaseListFilters, useListFilters } from "./use-list-filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Default sort settings for custom channels
const DEFAULT_SORT_COLUMN = "custom_channel";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";

// SessionStorage keys
const CUSTOM_CHANNEL_LIST_FILTERS_KEY = "custom-channels::list::filters";
const CUSTOM_CHANNEL_METRICS_KEY = "custom-channels::list::metrics";

// Default metrics for custom channels (excluding sourced_opportunities metrics)
const DEFAULT_METRICS: Array<keyof CustomChannelMetrics> = [
  "accounts_engaged",
  "total_engagements",
  "people_engaged",
];

// Interface for filter configuration
interface CustomChannelListFilters extends BaseListFilters {
  sortColumn?: string;
  sortDirection?: SortDirection;
  search?: string;
}

// Interface for query parameters
interface CustomChannelsQueryParams extends QueryParams {
  search?: string;
  sort_by?: string;
  sort_direction?: SortDirection;
  null_sort?: "max" | "min" | "first" | "last" | undefined;
  limit?: number;
  metrics?: Array<keyof CustomChannelMetrics>;
  metric_filters?: Record<string, unknown>;
}

/**
 * Hook for managing custom channel metrics
 * @returns Methods for getting and setting selected metrics
 */
export function useCustomChannelMetrics() {
  // Get and set metrics using sessionStorage for tab-specific behavior
  const [selectedMetrics, setSelectedMetrics] = useTrackedSessionStorage<
    Array<keyof CustomChannelMetrics>
  >(CUSTOM_CHANNEL_METRICS_KEY, DEFAULT_METRICS);

  const getDefaultMetrics = useCallback(() => DEFAULT_METRICS, []);

  return {
    selectedMetrics,
    setSelectedMetrics,
    getDefaultMetrics,
  };
}

/**
 * Hook for managing custom channel list filters
 * @returns Filters and utilities for managing custom channel list filters
 */
export function useCustomChannelListFilters() {
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams: getBaseQueryParams,
  } = useListFilters<
    CustomChannelListFilters,
    UnifiedTableColumn<CustomChannel>,
    CustomChannelsQueryParams
  >({
    storageKey: CUSTOM_CHANNEL_LIST_FILTERS_KEY,
    defaultSortColumn: DEFAULT_SORT_COLUMN,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    buildQueryParams,
  });

  // Helper function to build query params
  function buildQueryParams(
    filters: CustomChannelListFilters,
    options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<CustomChannel>[];
      limit?: number;
    },
  ): CustomChannelsQueryParams {
    const { searchTerm, columns = [], limit = 100 } = options || {};

    // Sort handling
    let sortCol = filters.sortColumn || DEFAULT_SORT_COLUMN;

    // Fallback if sort column no longer exists in current column set
    const validSortKeys = columns.map((c) => c.key);
    if (!validSortKeys.includes(sortCol)) {
      sortCol = DEFAULT_SORT_COLUMN;
    }

    const sortDir = filters.sortDirection || DEFAULT_SORT_DIRECTION;

    // Find column definition to get null_sort behaviour
    const column = columns.find((col) => col.key === sortCol);

    const params: CustomChannelsQueryParams = {
      sort_by: sortCol,
      sort_direction: sortDir,
      null_sort: column?.nullSort,
      search: searchTerm || filters.search || undefined,
      limit,
    };

    // Structured filters JSON
    const validColumnKeys = new Set(columns.map((c) => c.key.toString()));
    const group = buildFilterGroupFromColumnState(filters, validColumnKeys);
    if (group) params.filters = JSON.stringify(group);

    return params;
  }

  // Wrapper for getQueryParams that accepts metrics as a parameter
  const getQueryParams = useCallback(
    (options?: {
      searchTerm?: string;
      columns?: UnifiedTableColumn<CustomChannel>[];
      limit?: number;
      metrics?: Array<keyof CustomChannelMetrics>;
      metric_filters?: Record<string, unknown>;
    }) => {
      const { metrics, metric_filters, ...restOptions } = options || {};

      // Get base query params from filters
      const params = getBaseQueryParams(
        restOptions,
      ) as CustomChannelsQueryParams & {
        metrics?: Array<keyof CustomChannelMetrics>;
        metric_filters?: Record<string, unknown>;
      };

      // Add metrics and metric_filters if provided
      if (metrics) {
        params.metrics = metrics;
      }

      if (metric_filters) {
        params.metric_filters = metric_filters;
      }

      return params;
    },
    [getBaseQueryParams],
  );

  const getDefaultMetrics = useCallback(() => DEFAULT_METRICS, []);

  return {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
    getDefaultMetrics,
  };
}
